<template>
  <div class="container-fluid">
    <div class="row g-0">
      <div class="col-3">
        <search-sip @listen-sip-search="listenSearchEvent" class="search" />
      </div>
      <div class="col-5">
        <button class="btn btn-back btn-outline-secondary" @click="goBack">
          Back
        </button>

        <vue-good-table
          class="vue-table"
          ref="table"
          @row-click="transferStore.fetchCurrentSip"
          @page-change="onPageChange"
          @sort-change="onSortChange"
          @column-filter="onColumnFilter"
          @per-page-change="onPerPageChange"
          :isLoading.sync="isLoading"
          :pagination-options="paginationOptions"
          :totalRows="transferStore.totalRecordsSip"
          :rows="sipList"
          :columns="paginationData.columns"
        >
          <template #table-row="props">
            <span v-if="props.row.name == transferStore.currentSip.name">
              <span style="font-weight: bold; color: blue">{{
                props.formattedRow[props.column.field]
              }}</span>
            </span>

            <span v-else>{{ props.formattedRow[props.column.field] }}</span>
          </template>
        </vue-good-table>
      </div>
      <div class="col-4" :key="tableKey">
        <span v-show="sipList" class="border-start" id="sipLayout">
          <sip-layout />
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import GlobalData from "@/GlobalData";
import SipLayout from "@/components/ContentReview/Layout/SipLayout.vue";
import SearchSip from "@/components/ContentReview/SipDetail/SearchSip.vue";
import { useSortConversion } from "@/composables/useStringConversion";
import { useContentStore } from "@/stores/content_review/content";
import { useTransferStore } from "@/stores/content_review/transfer";
import { useAsyncState } from "@vueuse/core";
import { ref, watch, onMounted } from "vue";
import { useRouter } from "vue-router";

const tableKey = ref(0);
const table = ref();
const router = useRouter();
const transferStore = useTransferStore();
const contentStore = useContentStore();
const isLoading = ref(false);
const sipList: any = ref([]);

const forceRerender = () => {
  tableKey.value += 1;
};

onMounted(() => {
  GlobalData.getInstance().changeSipPosition = changeSipPosition;
});

watch(
  () => transferStore.loading,
  (updateLoading) => (isLoading.value = updateLoading)
);

watch(
  () => transferStore.sipList,
  (updateList) => {
    sipList.value = updateList;
  }
);

const paginationOptions = ref({
  enabled: true,
  mode: "pages",
  position: "bottom",
  perPageDropdown: [10, 20, 30],
  dropdownAllowAll: false,
});

const serverParams = ref({
  columnFilters: [
    { fieldName: "name", filterText: "" },
    { fieldName: "status", filterText: "" },
  ],
  sort: { field: "name", type: "asc" },
  page: 1,
  perPage: paginationOptions.value.perPageDropdown[1],
  // startingAfter: "",
});

let paginationData = {
  totalRecords: transferStore.totalRecordsSip,
  columns: [
    { label: "SIP Name", field: "name", sortable: false },
    { label: "SIP Status", field: "status", sortable: false },
  ],
};

watch(
  () => serverParams.value.columnFilters[0].filterText,
  () => {
    sipList.value = [];
    forceRerender();
  }
);

watch(
  () => serverParams.value.columnFilters[1].filterText,
  () => {
    sipList.value = [];
    forceRerender();
  }
);

const updateParams = (newProps: any) => {
  serverParams.value = Object.assign({}, serverParams.value, newProps);
};

const onPageChange = (params) => {
  updateParams({
    perPage: params.currentPerPage,
    page: params.currentPage,
    startingAfter: countStartingAfter(params),
  });
};

const onPerPageChange = (params) => {
  updateParams({
    perPage: params.currentPerPage,
    page: 1,
    startingAfter: "",
  });
  execute();
};

const onSortChange = (params) => {
  updateParams({
    sort: {
      type: params[0].type,
      field: params[0].field,
    },
    perPage: params.currentPerPage,
    page: 1,
    startingAfter: "",
  });
  execute();
};

const onColumnFilter = (params) => {
  table.value.reset();
  updateParams({
    sort: { type: "name", field: "asc" },
    page: 1,
    startingAfter: "",
  });
  execute();
};

const listenSearchEvent = (inputParams) => {
  // console.log("Sip Search:", inputParams);
  serverParams.value.columnFilters = serverParams.value.columnFilters.map(
    (obj) =>
      obj.fieldName === "name"
        ? { ...obj, filterText: inputParams.sipName }
        : obj
  );
  serverParams.value.columnFilters = serverParams.value.columnFilters.map(
    (obj) =>
      obj.fieldName === "status"
        ? { ...obj, filterText: inputParams.sipStatus }
        : obj
  );
  serverParams.value.columnFilters = serverParams.value.columnFilters.map(
    (obj) =>
      obj.fieldName === "transfer_id"
        ? { ...obj, filterText: inputParams.transferId }
        : obj
  );
  onColumnFilter(serverParams);
};

const { execute, error } = useAsyncState(() => {
  return transferStore.fetchSip(
    String(router.currentRoute.value.params.selectedTransferId),
    serverParams.value.perPage,
    useSortConversion(serverParams.value.sort) as string,
    serverParams.value.columnFilters as Array<object>
  );
}, null);

// const { execute, error } = useAsyncState(() => {
//   return transferStore.fetchSip(
//     String(router.currentRoute.value.params.selectedTransferId),
//     serverParams.value.columnFilters as Array<object>
//   );
// }, null);

const countStartingAfter = (params) => {
  if (serverParams.value.sort.field == "name") {
    if (serverParams.value.sort.type == "asc") {
      return params.currentPerPage * (params.currentPage - 1);
    } else if (serverParams.value.sort.type == "desc") {
      return (
        contentStore.totalRecords.valueOf() -
        params.currentPerPage * (params.currentPage - 1) +
        1
      );
    }
  } else return "";
};

const goBack = () => {
  router.push("/content");
};

const changeSipPosition = (remove, add) => {
  let sipLayout: any = document.getElementById("sipLayout");
  sipLayout.classList.remove(remove);
  sipLayout.classList.add(add);
};
</script>

<style scoped>
.vue-table {
  margin: 16px;
  top: 16px;
}
.border-start {
  position: fixed;
  top: 60px;
  overflow: auto;
  height: 100%;
}
.btn-back {
  float: left;
  margin: 16px;
  margin-bottom: 0;
  width: 100px;
  position: relative;
  z-index: 1;
}
.sip-layout {
  /* used to change style when modal is active */
  position: relative;
  overflow: auto;
  height: 100%;
}
.border-start {
  position: fixed;
  top: 60px;
  overflow: auto;
  height: 100%;
}
</style>
